import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { UserAvatar } from "@/components/TaskShared";
import { toast } from "sonner";
import { Crown, Lock, Plus, Pencil, Trash2, Loader2, Truck, Compass, UserRound, Check, ChevronsUpDown } from "lucide-react";

const SECTIONS = [
  { key: "delivery", label: "Delivery Management", icon: Truck, blurb: "Day-to-day execution and delivery workstreams." },
  { key: "strategic", label: "Strategic Management", icon: Compass, blurb: "Big-picture strategy, planning and initiatives." },
];
const STATUS_STYLE = {
  "Planned": "bg-[#F6F6FD] text-[#595D62] border-[#EEEFEF]",
  "In Progress": "bg-[#EDE7FF] text-[#5b2fd6] border-[#d9ccff]",
  "On Track": "bg-[#30BD79]/10 text-[#1f9d63] border-[#30BD79]/25",
  "At Risk": "bg-[#F59E0B]/12 text-[#b7791f] border-[#F59E0B]/30",
  "Complete": "bg-[#3B82F6]/10 text-[#1e5fbf] border-[#3B82F6]/25",
};

export default function CMOaaS() {
  const { visibleModules } = useWs();
  const allowed = (visibleModules || []).includes("cmoaas");

  if (!allowed) {
    return (
      <div>
        <PageHeader title="CMOaaS" subtitle="Chief Marketing Officer as a Service" />
        <div data-testid="cmoaas-no-access" className="rounded-3xl border border-[#EEEFEF] bg-white premium-shadow p-12 text-center max-w-xl mx-auto mt-10">
          <div className="h-14 w-14 rounded-2xl bg-[#F6F6FD] border border-[#EEEFEF] flex items-center justify-center mx-auto mb-5"><Lock className="h-6 w-6 text-[#9FA1A4]" /></div>
          <p className="font-display text-lg font-medium text-[#06060C]">You don't have access to see the CMOaaS projects.</p>
          <p className="text-sm text-[#9FA1A4] mt-2">Ask a super admin to enable CMOaaS for your role in the workspace settings.</p>
        </div>
      </div>
    );
  }
  return <CMOaaSBoard />;
}

function CMOaaSBoard() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("delivery");
  const [editing, setEditing] = useState(null); // { section } for new, or project for edit

  const load = async () => { try { const { data } = await api.get("/cmoaas/projects"); setData(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!(await confirmDelete({ message: "Remove this project?" }))) return; try { await api.delete(`/cmoaas/projects/${id}`); toast.success("Project removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  if (!data) return <div className="text-[#9FA1A4] text-sm">Loading…</div>;
  const canEdit = data.can_edit;
  const active = SECTIONS.find((s) => s.key === tab);
  const projects = data.sections[tab] || [];

  return (
    <div data-testid="cmoaas-board">
      <PageHeader title={<span className="flex items-center gap-2"><Crown className="h-6 w-6 text-[#723FFF]" /> CMOaaS</span>} subtitle="Chief Marketing Officer as a Service — delivery & strategic project management." />

      <div className="flex items-center gap-2 mb-6">
        {SECTIONS.map((s) => (
          <button key={s.key} data-testid={`cmoaas-tab-${s.key}`} onClick={() => setTab(s.key)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === s.key ? "bg-[#06060C] text-white" : "bg-white border border-[#EEEFEF] text-[#595D62] hover:bg-[#F6F6FD]"}`}>
            <s.icon className="h-4 w-4" /> {s.label}
            <span className={`font-mono-data text-[10px] rounded-full px-1.5 py-0.5 ${tab === s.key ? "bg-white/20" : "bg-[#F6F6FD]"}`}>{(data.sections[s.key] || []).length}</span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div><p className="text-sm font-medium text-[#06060C] flex items-center gap-2"><active.icon className="h-4 w-4 text-[#723FFF]" /> {active.label}</p><p className="text-xs text-[#9FA1A4] mt-0.5">{active.blurb}</p></div>
        {canEdit && <Button data-testid="cmoaas-add-project" onClick={() => setEditing({ section: tab })} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New project</Button>}
      </div>

      {projects.length === 0 ? (
        <div data-testid="cmoaas-empty" className="rounded-3xl border border-dashed border-[#e2e2e8] bg-[#FCFCFE] p-12 text-center">
          <div className="h-12 w-12 rounded-2xl bg-white border border-[#EEEFEF] flex items-center justify-center mx-auto mb-4"><active.icon className="h-5 w-5 text-[#c4c5cc]" /></div>
          <p className="text-sm text-[#595D62]">No {active.label.toLowerCase()} projects yet.</p>
          {canEdit && <button onClick={() => setEditing({ section: tab })} className="text-sm text-[#723FFF] hover:text-[#5b2fd6] mt-2">Add the first one</button>}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((p) => (
            <div key={p.id} data-testid="cmoaas-project-card" className="rounded-2xl border border-[#EEEFEF] bg-white premium-shadow p-4 flex flex-col">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`font-mono-data text-[10px] uppercase tracking-wider rounded-full border px-2 py-0.5 ${STATUS_STYLE[p.status] || STATUS_STYLE.Planned}`}>{p.status}</span>
                  {p.project_code && <span data-testid="cmoaas-project-code" className="font-mono-data text-[10px] text-[#5b2fd6] truncate">{p.project_code}</span>}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid="cmoaas-edit" onClick={() => setEditing(p)} className="text-[#c4c5cc] hover:text-[#723FFF]"><Pencil className="h-3.5 w-3.5" /></button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild><button data-testid="cmoaas-delete" className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button></AlertDialogTrigger>
                      <AlertDialogContent className="bg-white border-[#EEEFEF] text-[#06060C]"><AlertDialogHeader><AlertDialogTitle>Delete “{p.title}”?</AlertDialogTitle><AlertDialogDescription className="text-[#9FA1A4]">This removes the CMOaaS project.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel className="bg-transparent border-[#EEEFEF]">Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-rose-500 hover:bg-rose-600 text-white">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div>
                )}
              </div>
              <p className="text-sm font-medium text-[#06060C] mt-2.5">{p.title}</p>
              {p.description && <p className="text-xs text-[#595D62] mt-1 line-clamp-3 flex-1">{p.description}</p>}
              {p.owner_id ? (
                <p className="text-[11px] text-[#595D62] mt-3 flex items-center gap-1.5"><UserAvatar name={p.owner} uid={p.owner_id} hasAvatar={p.owner_has_avatar} size={18} /> {p.owner}</p>
              ) : p.owner ? (
                <p className="text-[11px] text-[#9FA1A4] mt-3 flex items-center gap-1.5"><UserRound className="h-3 w-3" /> {p.owner}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {editing && <ProjectDialog item={editing.id ? editing : null} section={editing.section || editing.section} statuses={data.statuses} defaultSection={editing.id ? editing.section : editing.section} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function OwnerCombobox({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [owners, setOwners] = useState([]);
  useEffect(() => { api.get("/cmoaas/assignable-owners").then(({ data }) => setOwners(data)).catch(() => setOwners([])); }, []);
  const selected = owners.find((o) => o.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="cmoaas-field-owner" className="w-full flex items-center justify-between gap-2 rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-left">
          {selected ? <span className="flex items-center gap-2 min-w-0"><UserAvatar name={selected.name} uid={selected.id} hasAvatar={selected.has_avatar} size={20} /><span className="truncate text-[#06060C]">{selected.name}</span></span> : <span className="text-[#9FA1A4]">Assign an agency user…</span>}
          <ChevronsUpDown className="h-4 w-4 text-[#9FA1A4] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] p-0 bg-white border-[#EEEFEF]">
        <Command>
          <CommandInput data-testid="cmoaas-owner-search" placeholder="Search agency users…" />
          <CommandList>
            <CommandEmpty>No agency users found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__unassigned" data-testid="cmoaas-owner-unassigned" onSelect={() => { onChange(null); setOpen(false); }}>
                <Check className={`h-4 w-4 mr-2 ${!value ? "opacity-100" : "opacity-0"}`} /> Unassigned
              </CommandItem>
              {owners.map((o) => (
                <CommandItem key={o.id} value={o.name} data-testid={`cmoaas-owner-option-${o.id}`} onSelect={() => { onChange(o.id); setOpen(false); }}>
                  <Check className={`h-4 w-4 mr-2 ${value === o.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="flex items-center gap-2"><UserAvatar name={o.name} uid={o.id} hasAvatar={o.has_avatar} size={22} /><span>{o.name}{o.job_title ? <span className="text-[#9FA1A4]"> · {o.job_title}</span> : ""}</span></span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ProjectDialog({ item, statuses, defaultSection, onClose, onSaved }) {
  const [form, setForm] = useState({ section: item?.section || defaultSection || "delivery", title: item?.title || "", status: item?.status || "Planned", owner_id: item?.owner_id || null, description: item?.description || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim()) { toast.error("Give the project a title"); return; }
    setSaving(true);
    try {
      if (item) await api.put(`/cmoaas/projects/${item.id}`, form); else await api.post("/cmoaas/projects", form);
      toast.success(item ? "Project updated" : "Project added"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-lg" data-testid="cmoaas-project-dialog">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit project" : "New CMOaaS project"}</DialogTitle><DialogDescription className="text-[#9FA1A4]">Standalone project tracked in CMOaaS.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Title</label><Input data-testid="cmoaas-field-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-[#9FA1A4] mb-1 block">Section</label>
              <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}><SelectTrigger data-testid="cmoaas-field-section" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]"><SelectItem value="delivery">Delivery Management</SelectItem><SelectItem value="strategic">Strategic Management</SelectItem></SelectContent></Select>
            </div>
            <div><label className="text-xs text-[#9FA1A4] mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="cmoaas-field-status" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger><SelectContent className="bg-white border-[#EEEFEF]">{statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Owner</label><OwnerCombobox value={form.owner_id} onChange={(v) => setForm({ ...form, owner_id: v })} /></div>
          <div><label className="text-xs text-[#9FA1A4] mb-1 block">Description</label><Textarea data-testid="cmoaas-field-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="bg-[#F6F6FD] border-[#EEEFEF] resize-none text-sm" /></div>
        </div>
        <DialogFooter><Button data-testid="cmoaas-save" onClick={save} disabled={saving} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
